import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  Stack,
  Paper,
  Chip,
  Alert,
  Snackbar,
  Grid,
} from "@mui/material";
//import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

import ContactInformation from "../../components/contact/contactInformation";
import ContactForm from "../../components/contact/contactForm";

export default function TalkToUs() {
  const theme = useTheme();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  return (
    <>
      <Helmet>
        <title>Fale Conosco | Clima Tropical Angola</title>

        <meta
          name="description"
          content="Entre em contato com a Clima Tropical em Angola. Solicite serviços de climatização, manutenção de ar condicionado, construção civil, serviços elétricos e soluções técnicas especializadas."
        />

        <meta
          name="keywords"
          content="Contato Clima Tropical, climatização Angola, ar condicionado Luanda, manutenção de ar condicionado, serviços elétricos Angola, construção civil Angola, assistência técnica climatização"
        />

        <link rel="canonical" href="https://climatropical.com/fale-conosco" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Fale Conosco | Clima Tropical Angola"
        />

        <meta
          property="og:description"
          content="Entre em contato com a Clima Tropical e descubra soluções profissionais em climatização, construção civil e serviços técnicos em Angola."
        />

        <meta
          property="og:url"
          content="https://climatropical.com/fale-conosco"
        />

        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content="Fale Conosco | Clima Tropical Angola"
        />

        <meta
          name="twitter:description"
          content="Solicite serviços profissionais de climatização, manutenção e construção civil em Angola."
        />
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          py: { xs: 4, md: 0 },
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
            color: "white",
            py: { xs: 6, md: 10 },
            mb: 8,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: "100%", mx: "auto" }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: theme.palette.info.light,
                }}
              >
                Fale Conosco
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                }}
              >
                Estamos aqui para ajudar! Entre em contato conosco e descubra
                como podemos transformar seu projeto em realidade com soluções
                de alta qualidade.
              </Typography>

              <Chip
                label="Resposta em até 24 horas"
                variant="filled"
                sx={{
                  backgroundColor: "white",
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  py: 1,
                }}
              />
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Main Content */}
          <Grid container spacing={4} alignItems="stretch">
            {/* Contact Form */}
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <ContactForm />
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <ContactInformation />
              </motion.div>
            </Grid>
          </Grid>

          {/* Additional Contact Options */}
          <Grid container spacing={4} sx={{ mt: 8 }}>
            <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 8 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  backgroundColor: theme.palette.primary.dark,
                  color: "white",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h4"
                  // gutterBottom
                  sx={{
                    fontWeight: "bold",
                    mb: 3,
                    color: theme.palette.info.main,
                  }}
                >
                  Por que nos contactar?
                </Typography>

                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                    <Typography variant="body2">
                      Orçamentos gratuitos e sem compromisso
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                    <Typography variant="body2">
                      Consultoria técnica especializada
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                    <Typography variant="body2">
                      Atendimento personalizado
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                    <Typography variant="body2">
                      Resposta rápida em até 24 horas
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <Card sx={{}}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                    }}
                  >
                    Atendimento de Emergência
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 4 }}>
                    Problemas urgentes com climatização ou sistemas elétricos?
                  </Typography>

                  <Button
                    component="a" // Transforma o botão em um link nativo
                    href="tel:+244941392971" // Remove os espaços e parênteses para o protocolo tel:
                    variant="contained"
                    size="medium"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      backgroundColor: theme.palette.error.main,
                      "&:hover": {},
                    }}
                  >
                    Ligar Agora: (+244) 941 392 971
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Success Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Mensagem enviada com sucesso! Entraremos em contato em breve.
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
